import { Anchor, Badge, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Select, Textarea, SegmentedControl, Radio, RadioGroup } from '@repletoryreach/sdk-ui/inputs';
import type { broadcasts, contacts, channelWa } from '@repletoryreach/sdk-api';
import { formatCount } from '../../../../lib/format';
import type { StepProps } from './shared';
import type { AudienceMode, Channel, EmailKind, SmsKind } from '../draft';

/**
 * A tag this big is very probably not what somebody meant to pick, and it is
 * the size at which a mistake stops being cheap. Not a limit: a plain warning,
 * with the number, so the decision stays with the person.
 */
const LARGE_AUDIENCE = 5_000;

/**
 * Step 1: who is this going to.
 *
 * Three answers, and a tag is the first because tags are how this product
 * groups contacts: assembling a group means tagging people and sending to it
 * means naming the tag. A segment is for a saved rule; the paste box is for the
 * genuinely ad-hoc handful.
 *
 * The three numbers under the choice come from the server, not from anything
 * counted here. `matched` is everyone the audience names, `opted_out` is how
 * many of those have asked not to be messaged, and `reachable` is the
 * difference. The middle one is the one people are surprised by, so it is
 * stated in its own line rather than left as the gap between two other figures.
 *
 * When the platform can send SMS (4 October 2026), the first question is how
 * it goes out. An SMS has no number to send from, and its kind decides who it
 * may reach, so the kind is asked here, beside the count it changes.
 *
 * Email (4 October 2026) is the same shape: offered only when the platform can
 * send email, no number, a kind that decides who it may reach, and a paste box
 * that takes addresses rather than numbers. Each channel is offered only while
 * it is available, and the choice appears once there is more than WhatsApp.
 */
export function AudienceStep({
  state, set, errors, t, smsAvailable, emailAvailable,
  numbers, numbersLoading, tags, tagsLoading, segments,
  preview, previewing, pastedCount, maxPasted
}: StepProps & {
  /** Whether to offer SMS at all. False keeps this step exactly as it was. */
  smsAvailable: boolean;
  /** Whether to offer email at all. */
  emailAvailable: boolean;
  numbers: channelWa.PhoneNumber[];
  numbersLoading: boolean;
  tags: contacts.TagSummary[];
  tagsLoading: boolean;
  segments: contacts.Segment[];
  preview: broadcasts.BroadcastPreview | null;
  previewing: boolean;
  pastedCount: number;
  maxPasted: number;
}) {
  const chosen = state.audienceMode === 'tag' ? state.contactTag
    : state.audienceMode === 'segment' ? state.segmentId
      : pastedCount > 0 ? 'pasted' : '';
  const isSms = smsAvailable && state.channel === 'sms';
  const isEmail = emailAvailable && state.channel === 'email';
  const channel: Channel = isSms ? 'sms' : isEmail ? 'email' : 'whatsapp';

  return (
    <Stack gap="lg">
      {(smsAvailable || emailAvailable) && (
        <Stack gap={6}>
          <Text role="inputLabel">{t('broadcasts.sms.channel.label')}</Text>
          <SegmentedControl
            value={channel}
            onChange={(v) => set('channel', (v === 'sms' || v === 'email' ? v : 'whatsapp') as Channel)}
            data={[
              { value: 'whatsapp', label: t('broadcasts.sms.channel.whatsapp') },
              ...(smsAvailable ? [{ value: 'sms', label: t('broadcasts.sms.channel.sms') }] : []),
              ...(emailAvailable ? [{ value: 'email', label: t('broadcasts.email.channel') }] : [])
            ]}
          />
        </Stack>
      )}

      {isEmail && (
        <RadioGroup label={t('broadcasts.email.kind.label')}>
          <Stack gap={8}>
            <Radio
              name="rr-bc-email-kind"
              value="marketing"
              checked={state.emailKind === 'marketing'}
              onChange={() => set('emailKind', 'marketing' as EmailKind)}
              label={t('broadcasts.sms.kind.marketing')}
              description={t('broadcasts.email.kind.marketingHint')}
            />
            <Radio
              name="rr-bc-email-kind"
              value="service"
              checked={state.emailKind === 'service'}
              onChange={() => set('emailKind', 'service' as EmailKind)}
              label={t('broadcasts.sms.kind.service')}
              description={t('broadcasts.email.kind.serviceHint')}
            />
          </Stack>
        </RadioGroup>
      )}

      {isSms && (
        /* Wired radio by radio, as on the When step: sdk-ui's `Radio` does
           not read the group's context. */
        <RadioGroup label={t('broadcasts.sms.kind.label')}>
          <Stack gap={8}>
            <Radio
              name="rr-bc-sms-kind"
              value="marketing"
              checked={state.smsKind === 'marketing'}
              onChange={() => set('smsKind', 'marketing' as SmsKind)}
              label={t('broadcasts.sms.kind.marketing')}
              description={t('broadcasts.sms.kind.marketingHint')}
            />
            <Radio
              name="rr-bc-sms-kind"
              value="service"
              checked={state.smsKind === 'service'}
              onChange={() => set('smsKind', 'service' as SmsKind)}
              label={t('broadcasts.sms.kind.service')}
              description={t('broadcasts.sms.kind.serviceHint')}
            />
          </Stack>
        </RadioGroup>
      )}

      {!isSms && !isEmail && (
        <Select
          label={t('broadcasts.form.phoneNumber')}
          description={numbers.length === 0 && !numbersLoading
            ? t('broadcasts.new.audience.noNumbers')
            : t('broadcasts.new.audience.numberHint')}
          error={errors.phoneNumberId}
          value={state.phoneNumberId}
          onChange={(e) => set('phoneNumberId', e.currentTarget.value)}
          data={[
            { value: '', label: t('common.select') },
            ...numbers.map((n) => ({ value: n.id, label: n.msisdn ?? n.display_name ?? n.id }))
          ]}
        />
      )}

      {/*
        * A tag list and a segment list that are simply empty read as a broken
        * control: the box opens, there is nothing in it, and nobody can tell
        * whether that is a fault or the truth. Say it once, above the choice,
        * and point at the two things that actually help: tag some contacts, or
        * message a few people directly, which is what someone with a handful
        * of contacts wanted in the first place.
        */}
      {!tagsLoading && tags.length === 0 && segments.length === 0 && (
        <Alert color="info">
          <Stack gap={4}>
            <Text role="bodySmall">{t('broadcasts.new.audience.nothingToSendTo')}</Text>
            <Group gap="xs">
              <Anchor href="/contacts">{t('broadcasts.new.audience.goTagContacts')}</Anchor>
              <Text role="caption" c="var(--rr-color-text-subtle)">{t('common.or')}</Text>
              <Anchor href="/inbox/compose">{t('broadcasts.new.audience.composeInstead')}</Anchor>
            </Group>
          </Stack>
        </Alert>
      )}

      <Stack gap={6}>
        <Text role="inputLabel">{t('broadcasts.form.audience')}</Text>
        <SegmentedControl
          value={state.audienceMode}
          onChange={(v) => set('audienceMode', v as AudienceMode)}
          data={[
            {
              value: 'tag',
              label: tags.length === 0 && !tagsLoading
                ? t('broadcasts.form.audience.tagEmpty')
                : t('broadcasts.form.audience.tag')
            },
            {
              value: 'segment',
              label: segments.length === 0
                ? t('broadcasts.form.audience.segmentEmpty')
                : t('broadcasts.form.audience.segment')
            },
            { value: 'paste', label: isEmail ? t('broadcasts.email.audience.paste') : t('broadcasts.form.audience.paste') }
          ]}
        />
      </Stack>

      {state.audienceMode === 'tag' ? (
        <Stack gap={6}>
          <Select
            label={t('broadcasts.form.tag')}
            description={t('broadcasts.form.tagHint')}
            error={errors.tag}
            value={state.contactTag}
            onChange={(e) => set('contactTag', e.currentTarget.value)}
            data={[
              { value: '', label: t('common.select') },
              ...tags.map((x) => ({
                value: x.tag,
                label: t('broadcasts.form.tag.option', { tag: x.tag, count: x.opted_in })
              }))
            ]}
          />
          {tags.length === 0 && !tagsLoading && <Alert color="info">{t('broadcasts.form.noTags')}</Alert>}
        </Stack>
      ) : state.audienceMode === 'segment' ? (
        <Stack gap={6}>
          <Select
            label={t('broadcasts.form.segment')}
            description={t('broadcasts.form.segmentHint')}
            error={errors.segment}
            value={state.segmentId}
            onChange={(e) => set('segmentId', e.currentTarget.value)}
            data={[
              { value: '', label: t('common.select') },
              ...segments.map((s) => ({ value: s.id, label: s.name }))
            ]}
          />
          {segments.length === 0 && <Alert color="info">{t('broadcasts.new.audience.noSegments')}</Alert>}
        </Stack>
      ) : (
        <Textarea
          label={isEmail
            ? t('broadcasts.email.recipients', { count: pastedCount })
            : t('broadcasts.form.recipients', { count: pastedCount })}
          description={isEmail
            ? t('broadcasts.email.pasteHint')
            : `${t('broadcasts.form.recipientsPlaceholder')} ${t('broadcasts.form.pasteHint')}`}
          error={errors.recipients}
          value={state.recipients}
          // Cut where the person can see it was cut. The saved draft has a size
          // limit and losing the whole draft to a 400 is worse than losing the
          // tail of a paste nobody meant to make this long.
          onChange={(e) => set('recipients', e.currentTarget.value.slice(0, maxPasted))}
          minRows={7}
        />
      )}

      {/* ── What that audience actually comes to ────────────────────────── */}
      {chosen && (
        <Card withBorder className="rr-bc-new__audience">
          {preview && !preview.refusal ? (
            <Stack gap={10}>
              <Group gap="sm" align="baseline">
                <Text role="headline">{formatCount(preview.reachable)}</Text>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">
                  {t('broadcasts.new.audience.willReceive')}
                </Text>
                {previewing && <Badge size="xs" variant="light">{t('common.loading')}</Badge>}
              </Group>

              <Text role="bodySmall" c="var(--rr-color-text-muted)">
                {t('broadcasts.new.audience.breakdown', {
                  matched: formatCount(preview.matched),
                  excluded: formatCount(preview.opted_out)
                })}
              </Text>

              {/* The number that surprises people, given its own sentence
                  rather than left as the gap between two others. */}
              {preview.opted_out > 0 && (
                <Alert color="info">
                  {isSms
                    ? t(state.smsKind === 'marketing' ? 'broadcasts.sms.audience.excludedMarketing' : 'broadcasts.sms.audience.excludedService',
                      { count: formatCount(preview.opted_out) })
                    : isEmail
                      ? t(state.emailKind === 'marketing' ? 'broadcasts.email.audience.excludedMarketing' : 'broadcasts.email.audience.excludedService',
                        { count: formatCount(preview.opted_out) })
                      : t('broadcasts.new.audience.optedOut', { count: formatCount(preview.opted_out) })}
                </Alert>
              )}

              {preview.reachable >= LARGE_AUDIENCE && (
                <Alert color="warning" title={t('broadcasts.new.audience.large.title')}>
                  {t('broadcasts.new.audience.large.body', { count: formatCount(preview.reachable) })}
                </Alert>
              )}
            </Stack>
          ) : preview?.refusal ? (
            <Alert color="warning" title={t('broadcasts.new.audience.cannotSend')}>
              {t(`broadcasts.new.refusal.${preview.refusal.code}`, preview.refusal.message)}
            </Alert>
          ) : (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('broadcasts.new.audience.counting')}</Text>
          )}
        </Card>
      )}
    </Stack>
  );
}
